import React, { useState, useMemo } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Layers,
  Lock,
  Network,
  FileText,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldAlert,
  Sliders,
  HelpCircle,
  ChevronRight,
  Plus,
  ShieldCheck,
  Check,
  ExternalLink,
  Clock,
  Filter,
  X,
  Building2,
  Globe,
  Key,
  CreditCard,
  LifeBuoy,
  History,
  FolderGit2,
  Zap,
  MoreHorizontal,
  Download,
  Mail,
  ChevronDown,
  UserPlus,
  Trash2,
  Power,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Radio,
  SlidersHorizontal,
  ChevronLeft,
  Shield,
  UserCheck,
  Settings,
  Edit3,
  Eye,
  KeyRound,
  Fingerprint,
  Info,
  Server,
  Terminal,
  Cpu,
  FileCheck,
  Workflow,
  Sparkles,
  Play,
  ArrowRight,
  GitBranch,
  Gauge,
  BarChart3,
  HardDrive,
  Copy,
  BookOpen,
  ArrowUpRight,
  LockKeyhole
} from 'lucide-react';
import { AppView } from '../../types';

interface AiPlatformManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type AiPlatformTab =
  | 'overview'
  | 'models'
  | 'routes'
  | 'prompts'
  | 'evaluations'
  | 'rag'
  | 'costs';

export interface ModelRegistryItem {
  id: string;
  name: string;
  version: string;
  provider: string;
  providerType: 'Yamoo Internal vLLM' | 'Enterprise Gateway' | 'Self-Hosted Cluster' | 'Regional Sovereign';
  status: 'Operational' | 'Testing' | 'Degraded' | 'Offline';
  latencyP95: number; // ms
  costPer1kInput: number; // $
  costPer1kOutput: number; // $
  contextWindow: string;
  tpmLimit: string;
  lastUpdated: string;
  isPrimary: boolean;
}

export interface ModelRouteItem {
  id: string;
  routeKey: string;
  task: string;
  primaryModelId: string;
  primaryModelName: string;
  fallbackModelId: string;
  fallbackModelName: string;
  health: 'Operational' | 'Degraded' | 'Healthy';
  p95Latency: number; // ms
  evalScore: number; // e.g. 98.4
  costPer1kReq: number; // $
  status: 'Active' | 'Canary 10%' | 'Fallback Active' | 'Deprecated';
  trafficWeight: string;
  timeoutMs: number;
}

export interface PromptItem {
  id: string;
  key: string;
  name: string;
  version: string;
  task: string;
  status: 'Production' | 'Staged' | 'Archived' | 'Evaluation';
  evalScore: number; // e.g. 99.1
  latencyAverage: number; // ms
  tokenEstimate: number;
  lastModified: string;
  modifiedBy: string;
  templateSnippet: string;
  targetRoute: string;
  hasCoTHidden: boolean;
}

export interface EvalSuiteItem {
  id: string;
  suiteName: string;
  taskDomain: string;
  datasetSize: number;
  exactMatchScore: number; // %
  hallucinationRate: number; // %
  p99Latency: number; // ms
  safetyCompliance: number; // %
  status: 'Passed' | 'Warning' | 'Running' | 'Failed';
  lastRunDate: string;
  testedModel: string;
}

export interface RagSourceItem {
  id: string;
  name: string;
  type: 'Tax & Salary Regulatory Corpus' | 'API Schema & Doc Embeddings' | 'SLA & Customer Policy Vector Store' | 'Compliance Standard Ledger';
  vectorCount: string;
  embeddingModel: string;
  indexLatency: string;
  hitRate30d: number; // %
  lastSync: string;
  status: 'Synchronized' | 'Re-indexing' | 'Operational';
}

export interface AiAuditLogItem {
  id: string;
  timestamp: string;
  actor: string;
  nativeAction: string;
  targetResource: string;
  result: 'Success' | 'Denied (Policy)' | 'Warning';
  details: string;
}

export const AiPlatformManagement: React.FC<AiPlatformManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('data');
  const [activeTab, setActiveTab] = useState<AiPlatformTab>('overview');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Platform Configuration States
  const [routingLogic, setRoutingLogic] = useState<'cost' | 'latency' | 'isolation'>('latency');
  const [piiRedaction, setPiiRedaction] = useState<boolean>(true);
  const [promptInjectionGuard, setPromptInjectionGuard] = useState<boolean>(true);
  const [regionalResidencyLock, setRegionalResidencyLock] = useState<boolean>(false);

  // Modals
  const [showNewRouteModal, setShowNewRouteModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showPromptCompareModal, setShowPromptCompareModal] = useState<boolean>(false);
  const [selectedPromptForCompare, setSelectedPromptForCompare] = useState<PromptItem | null>(null);
  const [showRunEvalModal, setShowRunEvalModal] = useState<boolean>(false);
  const [evalModalSuite, setEvalModalSuite] = useState<string>('eval-salary-accuracy-v4');

  // Form states for New Route
  const [newRouteKey, setNewRouteKey] = useState<string>('');
  const [newRouteTask, setNewRouteTask] = useState<string>('Calculation Rule Reasoning');
  const [newRoutePrimary, setNewRoutePrimary] = useState<string>('yamoo-core-reasoner-v3');
  const [newRouteFallback, setNewRouteFallback] = useState<string>('yamoo-fast-router-v2');

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // 1. Dynamic Model Registry (Provider-neutral, YamooPay-first)
  const [modelRegistry, setModelRegistry] = useState<ModelRegistryItem[]>([
    {
      id: 'yamoo-core-reasoner-v3',
      name: 'Yamoo Core Reasoner',
      version: 'v3.4.2-prod',
      provider: 'Yamoo AI Cloud',
      providerType: 'Yamoo Internal vLLM',
      status: 'Operational',
      latencyP95: 380,
      costPer1kInput: 0.0012,
      costPer1kOutput: 0.0036,
      contextWindow: '128k tokens',
      tpmLimit: '2.5M TPM',
      lastUpdated: '2 hours ago',
      isPrimary: true
    },
    {
      id: 'yamoo-fast-router-v2',
      name: 'Yamoo Fast Router',
      version: 'v2.1.0-live',
      provider: 'Yamoo AI Cloud',
      providerType: 'Yamoo Internal vLLM',
      status: 'Operational',
      latencyP95: 115,
      costPer1kInput: 0.0003,
      costPer1kOutput: 0.0009,
      contextWindow: '32k tokens',
      tpmLimit: '8.0M TPM',
      lastUpdated: '4 hours ago',
      isPrimary: false
    },
    {
      id: 'yamoo-compliance-guard-v1',
      name: 'Yamoo Compliance Guard',
      version: 'v1.8.0-sovereign',
      provider: 'EU Sovereign Cluster (Frankfurt)',
      providerType: 'Regional Sovereign',
      status: 'Operational',
      latencyP95: 220,
      costPer1kInput: 0.0008,
      costPer1kOutput: 0.0024,
      contextWindow: '64k tokens',
      tpmLimit: '3.0M TPM',
      lastUpdated: '1 day ago',
      isPrimary: false
    },
    {
      id: 'enterprise-deep-engine-v2',
      name: 'Enterprise Multi-Modal Gateway',
      version: '2026-08-preview',
      provider: 'Yamoo Enterprise Dedicated',
      providerType: 'Enterprise Gateway',
      status: 'Operational',
      latencyP95: 490,
      costPer1kInput: 0.0025,
      costPer1kOutput: 0.0075,
      contextWindow: '200k tokens',
      tpmLimit: '1.8M TPM',
      lastUpdated: '3 days ago',
      isPrimary: false
    },
    {
      id: 'sandbox-quant-model-70b',
      name: 'Self-Hosted Quant 70B',
      version: 'q4_k_m-vllm',
      provider: 'Internal GPU Pod (GKE-US)',
      providerType: 'Self-Hosted Cluster',
      status: 'Testing',
      latencyP95: 540,
      costPer1kInput: 0.0004,
      costPer1kOutput: 0.0008,
      contextWindow: '64k tokens',
      tpmLimit: '1.2M TPM',
      lastUpdated: '5 days ago',
      isPrimary: false
    }
  ]);

  // 2. Real Model Routes
  const [modelRoutes, setModelRoutes] = useState<ModelRouteItem[]>([
    {
      id: 'rt-1',
      routeKey: 'route.salary.calculation_reasoning',
      task: 'Salary & Payroll Tax Formula Synthesis',
      primaryModelId: 'yamoo-core-reasoner-v3',
      primaryModelName: 'Yamoo Core Reasoner (v3.4.2)',
      fallbackModelId: 'yamoo-fast-router-v2',
      fallbackModelName: 'Yamoo Fast Router (v2.1.0)',
      health: 'Operational',
      p95Latency: 395,
      evalScore: 99.4,
      costPer1kReq: 0.048,
      status: 'Active',
      trafficWeight: '100% Primary',
      timeoutMs: 1500
    },
    {
      id: 'rt-2',
      routeKey: 'route.tax.multi_jurisdiction_rules',
      task: 'Cross-Border VAT & Withholding Classification',
      primaryModelId: 'yamoo-compliance-guard-v1',
      primaryModelName: 'Yamoo Compliance Guard (v1.8.0)',
      fallbackModelId: 'yamoo-core-reasoner-v3',
      fallbackModelName: 'Yamoo Core Reasoner (v3.4.2)',
      health: 'Operational',
      p95Latency: 240,
      evalScore: 99.8,
      costPer1kReq: 0.032,
      status: 'Active',
      trafficWeight: '100% Primary',
      timeoutMs: 1200
    },
    {
      id: 'rt-3',
      routeKey: 'route.support.intent_classifier',
      task: 'Customer Support Intent & SLA Triage',
      primaryModelId: 'yamoo-fast-router-v2',
      primaryModelName: 'Yamoo Fast Router (v2.1.0)',
      fallbackModelId: 'yamoo-core-reasoner-v3',
      fallbackModelName: 'Yamoo Core Reasoner (v3.4.2)',
      health: 'Operational',
      p95Latency: 110,
      evalScore: 98.9,
      costPer1kReq: 0.012,
      status: 'Active',
      trafficWeight: '100% Primary',
      timeoutMs: 800
    },
    {
      id: 'rt-4',
      routeKey: 'route.developer.api_doc_query',
      task: 'Developer Portal Semantic Code Assistant',
      primaryModelId: 'enterprise-deep-engine-v2',
      primaryModelName: 'Enterprise Multi-Modal Gateway',
      fallbackModelId: 'yamoo-core-reasoner-v3',
      fallbackModelName: 'Yamoo Core Reasoner (v3.4.2)',
      health: 'Operational',
      p95Latency: 480,
      evalScore: 97.6,
      costPer1kReq: 0.085,
      status: 'Canary 10%',
      trafficWeight: '90% Core / 10% Deep',
      timeoutMs: 2500
    },
    {
      id: 'rt-5',
      routeKey: 'route.compliance.sanctions_sanitizer',
      task: 'Audit Metadata & PII Sanitization Guard',
      primaryModelId: 'yamoo-compliance-guard-v1',
      primaryModelName: 'Yamoo Compliance Guard (v1.8.0)',
      fallbackModelId: 'yamoo-fast-router-v2',
      fallbackModelName: 'Yamoo Fast Router (v2.1.0)',
      health: 'Operational',
      p95Latency: 185,
      evalScore: 99.9,
      costPer1kReq: 0.026,
      status: 'Active',
      trafficWeight: '100% Sovereign',
      timeoutMs: 1000
    }
  ]);

  // 3. Prompt Library (CRITICAL: Zero private CoT exposed)
  const [promptLibrary, setPromptLibrary] = useState<PromptItem[]>([
    {
      id: 'pm-1',
      key: 'prompt.salary.bracket_calculation',
      name: 'Salary Bracket & Progressive Tax Calculator',
      version: 'v3.2.1',
      task: 'Salary & Progressive Tax Synthesis',
      status: 'Production',
      evalScore: 99.4,
      latencyAverage: 375,
      tokenEstimate: 420,
      lastModified: '2026-08-26',
      modifiedBy: 'Harry Chama',
      templateSnippet: 'System: You are YamooPay Calculation Engine. Given gross salary {gross} and jurisdiction {country_code}, output exact progressive deduction tiers according to fiscal year 2026 statutory rates. Return strict JSON format: { "brackets": [], "net": number }',
      targetRoute: 'route.salary.calculation_reasoning',
      hasCoTHidden: true
    },
    {
      id: 'pm-2',
      key: 'prompt.vat.reverse_charge_evaluator',
      name: 'B2B Reverse Charge & Exemption Resolver',
      version: 'v2.8.0',
      task: 'VAT & Cross-Border Exemption Logic',
      status: 'Production',
      evalScore: 99.8,
      latencyAverage: 215,
      tokenEstimate: 310,
      lastModified: '2026-08-24',
      modifiedBy: 'Sarah Jenkins',
      templateSnippet: 'System: Verify VIES registration for entity {vat_id} in {origin_country} to {destination_country}. Enforce Article 194 EU VAT Directive validation. Output boolean { "reverse_charge_applicable": true, "legal_citation": string }',
      targetRoute: 'route.tax.multi_jurisdiction_rules',
      hasCoTHidden: true
    },
    {
      id: 'pm-3',
      key: 'prompt.support.sentiment_triage',
      name: 'Enterprise Support Intent & SLA Escalator',
      version: 'v4.0.0',
      task: 'Support Intent & Priority Classification',
      status: 'Production',
      evalScore: 98.9,
      latencyAverage: 105,
      tokenEstimate: 180,
      lastModified: '2026-08-20',
      modifiedBy: 'Chloe Dupont',
      templateSnippet: 'System: Classify customer ticket urgency [P1_CRITICAL, P2_HIGH, P3_NORMAL]. If keywords indicate API outage or authentication failure for Enterprise SLA, trigger immediate SRE webhook notification.',
      targetRoute: 'route.support.intent_classifier',
      hasCoTHidden: true
    },
    {
      id: 'pm-4',
      key: 'prompt.developer.schema_doc_generator',
      name: 'OpenAPI Spec Generator for Custom Formulas',
      version: 'v1.4.0-staged',
      task: 'Developer Code & Schema Synthesis',
      status: 'Staged',
      evalScore: 97.6,
      latencyAverage: 460,
      tokenEstimate: 620,
      lastModified: '2026-08-27',
      modifiedBy: 'Kenji Sato',
      templateSnippet: 'System: Generate strict TypeScript type signatures and REST payload schemas matching YamooPay SDK specifications for custom enterprise tax formula {formula_ast}.',
      targetRoute: 'route.developer.api_doc_query',
      hasCoTHidden: true
    }
  ]);

  // 4. Real Evaluation Suites
  const [evalSuites, setEvalSuites] = useState<EvalSuiteItem[]>([
    {
      id: 'eval-1',
      suiteName: 'Salary Calculation Exactness Benchmark v4',
      taskDomain: 'Payroll & Statutory Tax',
      datasetSize: 5000,
      exactMatchScore: 99.4,
      hallucinationRate: 0.02,
      p99Latency: 520,
      safetyCompliance: 100.0,
      status: 'Passed',
      lastRunDate: '2026-08-27 18:30 UTC',
      testedModel: 'Yamoo Core Reasoner (v3.4.2)'
    },
    {
      id: 'eval-2',
      suiteName: 'Cross-Border VAT Statutory Edge Cases 2026',
      taskDomain: 'Tax Compliance & VIES',
      datasetSize: 3200,
      exactMatchScore: 99.8,
      hallucinationRate: 0.01,
      p99Latency: 310,
      safetyCompliance: 100.0,
      status: 'Passed',
      lastRunDate: '2026-08-26 22:15 UTC',
      testedModel: 'Yamoo Compliance Guard (v1.8.0)'
    },
    {
      id: 'eval-3',
      suiteName: 'PII Leakage & Data Sanitization Adversarial Suite',
      taskDomain: 'Security & Redaction',
      datasetSize: 8400,
      exactMatchScore: 99.9,
      hallucinationRate: 0.00,
      p99Latency: 280,
      safetyCompliance: 100.0,
      status: 'Passed',
      lastRunDate: '2026-08-28 01:00 UTC',
      testedModel: 'Yamoo Compliance Guard (v1.8.0)'
    },
    {
      id: 'eval-4',
      suiteName: 'Prompt Injection & Jailbreak Defense Suite v5',
      taskDomain: 'AI Safety & Guardrails',
      datasetSize: 12500,
      exactMatchScore: 99.2,
      hallucinationRate: 0.03,
      p99Latency: 190,
      safetyCompliance: 99.9,
      status: 'Passed',
      lastRunDate: '2026-08-28 03:45 UTC',
      testedModel: 'Yamoo Fast Router (v2.1.0)'
    }
  ]);

  // 5. RAG & Knowledge Sources
  const [ragSources, setRagSources] = useState<RagSourceItem[]>([
    {
      id: 'rag-1',
      name: 'Global Tax & Statutory Labor Law Knowledge Corpus (2026)',
      type: 'Tax & Salary Regulatory Corpus',
      vectorCount: '1.42M vectors',
      embeddingModel: 'yamoo-embed-dense-v2',
      indexLatency: '18ms P95',
      hitRate30d: 99.3,
      lastSync: '15 mins ago',
      status: 'Operational'
    },
    {
      id: 'rag-2',
      name: 'YamooPay Developer API v3 Reference & Webhook Schemas',
      type: 'API Schema & Doc Embeddings',
      vectorCount: '340k vectors',
      embeddingModel: 'yamoo-embed-dense-v2',
      indexLatency: '12ms P95',
      hitRate30d: 98.7,
      lastSync: '1 hour ago',
      status: 'Operational'
    },
    {
      id: 'rag-3',
      name: 'Enterprise SLA & Multi-Jurisdiction Compliance Standards',
      type: 'Compliance Standard Ledger',
      vectorCount: '620k vectors',
      embeddingModel: 'yamoo-embed-dense-v2',
      indexLatency: '16ms P95',
      hitRate30d: 99.6,
      lastSync: '4 hours ago',
      status: 'Operational'
    }
  ]);

  // 6. Recent AI Platform Actions / Audit Log (Matching bottom section of Stitch reference)
  const [auditLogs, setAuditLogs] = useState<AiAuditLogItem[]>([
    {
      id: 'log-1',
      timestamp: '2026-08-28 04:14:02',
      actor: 'sysadmin@yamoopay.com',
      nativeAction: 'UpdateModelConfig',
      targetResource: 'yamoo-core-reasoner-v3',
      result: 'Success',
      details: 'Adjusted P95 timeout threshold from 2000ms to 1500ms on production cluster.'
    },
    {
      id: 'log-2',
      timestamp: '2026-08-28 02:40:15',
      actor: 'automated_router',
      nativeAction: 'ChangeAIRoute',
      targetResource: 'eu-west-sovereign-gateway',
      result: 'Success',
      details: 'Traffic routed to secondary regional cluster during scheduled container rolling upgrade.'
    },
    {
      id: 'log-3',
      timestamp: '2026-08-27 21:05:12',
      actor: 'j.doe@yamoopay.com',
      nativeAction: 'DisableSafetyFilter',
      targetResource: 'PII_Redaction_Global',
      result: 'Denied (Policy)',
      details: 'Attempted to disable global PII redaction without CISO step-up approval.'
    },
    {
      id: 'log-4',
      timestamp: '2026-08-27 18:22:40',
      actor: 'harryeliechama@gmail.com',
      nativeAction: 'PromotePromptVersion',
      targetResource: 'prompt.salary.bracket_calculation',
      result: 'Success',
      details: 'Promoted version v3.2.1 to production after 99.4% eval suite pass.'
    }
  ]);

  // Handle New Route creation
  const handleCreateRoute = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRouteKey.trim()) {
      triggerToast('Please provide a valid internal route key.');
      return;
    }

    const primaryModelObj = modelRegistry.find((m) => m.id === newRoutePrimary) || modelRegistry[0];
    const fallbackModelObj = modelRegistry.find((m) => m.id === newRouteFallback) || modelRegistry[1];

    const newRoute: ModelRouteItem = {
      id: `rt-${Date.now()}`,
      routeKey: newRouteKey.trim(),
      task: newRouteTask.trim(),
      primaryModelId: primaryModelObj.id,
      primaryModelName: `${primaryModelObj.name} (${primaryModelObj.version})`,
      fallbackModelId: fallbackModelObj.id,
      fallbackModelName: `${fallbackModelObj.name} (${fallbackModelObj.version})`,
      health: 'Operational',
      p95Latency: primaryModelObj.latencyP95,
      evalScore: 99.1,
      costPer1kReq: (primaryModelObj.costPer1kInput + primaryModelObj.costPer1kOutput) * 10,
      status: 'Active',
      trafficWeight: '100% Primary',
      timeoutMs: 1500
    };

    setModelRoutes([newRoute, ...modelRoutes]);
    setShowNewRouteModal(false);
    setNewRouteKey('');
    triggerToast(`AI Gateway route "${newRoute.routeKey}" provisioned successfully!`);
  };

  // Handle Apply Changes for Platform Configuration
  const handleApplyConfigChanges = () => {
    const newLog: AiAuditLogItem = {
      id: `log-${Date.now()}`,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
      actor: 'Admin User (You)',
      nativeAction: 'UpdatePlatformGatewayConfig',
      targetResource: 'Global_AI_Gateway_Policy',
      result: 'Success',
      details: `Routing logic updated to [${routingLogic}]. PII Redaction: ${piiRedaction ? 'ON' : 'OFF'}, Prompt Guard: ${promptInjectionGuard ? 'ON' : 'OFF'}, Data Residency: ${regionalResidencyLock ? 'LOCKED' : 'STANDARD'}.`
    };

    setAuditLogs([newLog, ...auditLogs]);
    triggerToast('Platform routing configuration & security filters applied globally!');
  };

  // Handle Prompt Promotion
  const handlePromotePrompt = (prompt: PromptItem) => {
    const updated = promptLibrary.map((p) => {
      if (p.id === prompt.id) {
        return { ...p, status: 'Production' as const };
      }
      return p;
    });
    setPromptLibrary(updated);
    triggerToast(`Prompt "${prompt.name}" (${prompt.version}) promoted to Production.`);
  };

  // Handle Prompt Rollback
  const handleRollbackPrompt = (prompt: PromptItem) => {
    triggerToast(`Rollback initialized for "${prompt.key}". Restoring previous stable version snapshot.`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Admin Console
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                  onNavigateToView('admin-organizations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>

              {/* Sub-navigation under Data & AI */}
              {selectedDomain === 'data' && (
                <div className="ml-4 pl-3 border-l-2 border-[#2563EB] my-1 space-y-1">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#2563EB] bg-blue-50 transition-colors cursor-pointer"
                  >
                    AI Platform
                  </button>
                  <button
                    onClick={() => onNavigateToView('enterprise-data-catalog')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Data Catalog
                  </button>
                  <button
                    onClick={() => onNavigateToView('caio-command-center')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    CAIO Command
                  </button>
                  <button
                    onClick={() => onNavigateToView('ai-sandbox-playground')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    AI Sandbox
                  </button>
                </div>
              )}
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                  onNavigateToView('admin-compliance');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                  onNavigateToView('admin-roles-permissions');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Sidebar Footer Profile (matching screenshot) */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-emerald-400 font-bold text-xs flex items-center justify-center border border-emerald-500/30">
              AD
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Admin User</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">System Administrator</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR (matching screenshot) */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Breadcrumb Header in topbar */}
          <div className="flex items-center gap-2 text-xs text-[#64748B] font-medium">
            <span>Data & AI</span>
            <ChevronRight className="w-3 h-3 text-[#CBD5E1]" />
            <span className="font-bold text-[#0F172A]">AI Platform</span>
          </div>

          {/* Search Input in topbar */}
          <div className="flex items-center gap-4 flex-1 max-w-md mx-6">
            <div className="relative w-full">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources, models, routes, or prompts..."
                className="w-full pl-9 pr-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] placeholder-[#94A3B8] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Topbar Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('AI Gateway Health: 100% Operational across all regional clusters')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Gateway Health"
            >
              <Zap className="w-4 h-4 text-amber-500" />
            </button>
            <button
              onClick={() => triggerToast('No pending AI model alerts or failover events')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigateToView('help-center')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Help & Documentation"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white font-bold text-xs flex items-center justify-center shadow-xs">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Title and Primary Action Row (matching screenshot) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                AI Platform
              </h1>
              <p className="text-xs text-[#64748B] font-medium mt-1">
                Manage AI gateways, model routing, and performance telemetry.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowExportModal(true)}
                className="px-4 py-2.5 bg-white hover:bg-[#F8FAFC] text-[#334155] border border-[#E2E8F0] font-bold text-xs rounded-xl shadow-2xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Download className="w-4 h-4 text-[#64748B]" />
                <span>Export Telemetry</span>
              </button>

              <button
                onClick={() => setShowNewRouteModal(true)}
                className="px-4 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4 text-white" />
                <span>New Route</span>
              </button>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 4 TOP METRIC CARDS (Exact match to Stitch screenshot)           */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. TOTAL REQUESTS */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs relative">
              <div className="flex items-center justify-between text-[#64748B] text-[11px] font-bold uppercase tracking-wider">
                <span>TOTAL REQUESTS</span>
                <Sparkles className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-black text-[#0F172A]">1.2M</span>
                <span className="text-xs font-bold text-emerald-600 inline-flex items-center">
                  ↑ 12%
                </span>
              </div>
              <span className="text-[11px] text-[#94A3B8] font-medium block mt-1">
                Last 30 days
              </span>
            </div>

            {/* 2. AVG LATENCY */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs relative">
              <div className="flex items-center justify-between text-[#64748B] text-[11px] font-bold uppercase tracking-wider">
                <span>AVG LATENCY</span>
                <Gauge className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-black text-[#0F172A]">420ms</span>
                <span className="text-xs font-bold text-red-500 inline-flex items-center">
                  ↑ 5%
                </span>
              </div>
              <span className="text-[11px] text-[#94A3B8] font-medium block mt-1">
                Across all models
              </span>
            </div>

            {/* 3. TOKEN USAGE */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs relative">
              <div className="flex items-center justify-between text-[#64748B] text-[11px] font-bold uppercase tracking-wider">
                <span>TOKEN USAGE</span>
                <RefreshCw className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-black text-[#0F172A]">850M</span>
              </div>
              <span className="text-[11px] text-[#94A3B8] font-medium block mt-1">
                Combined input/output
              </span>
            </div>

            {/* 4. MODEL SUCCESS RATE */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs relative">
              <div className="flex items-center justify-between text-[#64748B] text-[11px] font-bold uppercase tracking-wider">
                <span>MODEL SUCCESS RATE</span>
                <CheckCircle2 className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-black text-[#0F172A]">99.8%</span>
                <span className="text-[11px] text-[#64748B] font-medium">
                  Target: &gt;99.5%
                </span>
              </div>
              <span className="text-[11px] text-emerald-600 font-bold block mt-1">
                Status: Healthy
              </span>
            </div>
          </div>

          {/* Sub-Views Tabs Bar */}
          <div className="flex items-center gap-2 border-b border-[#E2E8F0] pb-2 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('routes')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'routes'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Model Routes ({modelRoutes.length})
            </button>
            <button
              onClick={() => setActiveTab('prompts')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'prompts'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Prompt Library ({promptLibrary.length})
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'models'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Model Registry ({modelRegistry.length})
            </button>
            <button
              onClick={() => setActiveTab('evaluations')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'evaluations'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Evaluations ({evalSuites.length})
            </button>
            <button
              onClick={() => setActiveTab('rag')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'rag'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              RAG & Knowledge ({ragSources.length})
            </button>
            <button
              onClick={() => setActiveTab('costs')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'costs'
                  ? 'bg-[#0F172A] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
              }`}
            >
              Costs & Usage
            </button>
          </div>

          {/* =============================================================== */}
          {/* TAB 1: OVERVIEW (Exact Side-by-Side match to Stitch Screenshot) */}
          {/* =============================================================== */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* ----------------------------------------------------------- */}
                {/* LEFT COLUMN: MODEL REGISTRY TABLE (7 Cols in 12-grid)       */}
                {/* ----------------------------------------------------------- */}
                <div className="lg:col-span-7 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                  <div className="p-4 border-b border-[#F1F5F9] flex items-center justify-between bg-white">
                    <span className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                      Model Registry
                    </span>
                    <button
                      onClick={() => setActiveTab('models')}
                      className="text-xs font-bold text-[#2563EB] hover:underline cursor-pointer"
                    >
                      Manage All Models
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-[#F1F5F9] text-[#64748B] font-bold text-[11px] bg-[#F8FAFC]/50">
                          <th className="py-3 px-4">Model Name</th>
                          <th className="py-3 px-4">Version</th>
                          <th className="py-3 px-4">Provider</th>
                          <th className="py-3 px-3 text-center">Status</th>
                          <th className="py-3 px-4 text-right">Last Updated</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F1F5F9]">
                        {modelRegistry.map((model) => (
                          <tr key={model.id} className="hover:bg-[#F8FAFC] transition-colors">
                            {/* Model Name */}
                            <td className="py-3.5 px-4 font-bold text-[#0F172A]">
                              <div className="flex items-center gap-2">
                                <Cpu className="w-3.5 h-3.5 text-[#64748B]" />
                                <span>{model.name}</span>
                              </div>
                            </td>

                            {/* Version */}
                            <td className="py-3.5 px-4 font-mono text-[#64748B] text-[11px]">
                              {model.version}
                            </td>

                            {/* Provider */}
                            <td className="py-3.5 px-4 text-[#475569]">
                              {model.provider}
                            </td>

                            {/* Status */}
                            <td className="py-3.5 px-3 text-center">
                              {model.status === 'Operational' ? (
                                <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1 border border-emerald-200/50">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                  Operational
                                </span>
                              ) : model.status === 'Testing' ? (
                                <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1 border border-blue-200/50">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                                  Testing
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1 border border-amber-200/50">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                  {model.status}
                                </span>
                              )}
                            </td>

                            {/* Last Updated */}
                            <td className="py-3.5 px-4 text-right text-[#64748B] text-[11px]">
                              {model.lastUpdated}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ----------------------------------------------------------- */}
                {/* RIGHT COLUMN: PLATFORM CONFIGURATION (5 Cols in 12-grid)    */}
                {/* ----------------------------------------------------------- */}
                <div className="lg:col-span-5 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs p-5 space-y-6">
                  <div>
                    <h3 className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                      Platform Configuration
                    </h3>
                  </div>

                  {/* GATEWAY ROUTING LOGIC */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                      GATEWAY ROUTING LOGIC
                    </label>

                    <div className="space-y-2.5">
                      {/* Option 1: Cost-Optimized */}
                      <label className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-[#F8FAFC] transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="routingLogic"
                          checked={routingLogic === 'cost'}
                          onChange={() => setRoutingLogic('cost')}
                          className="mt-0.5 text-[#2563EB] focus:ring-[#2563EB]"
                        />
                        <div>
                          <span className="text-xs font-bold text-[#0F172A] block">
                            Cost-Optimized
                          </span>
                          <span className="text-[11px] text-[#64748B] leading-relaxed block">
                            Routes to cheapest available model that meets task latency criteria.
                          </span>
                        </div>
                      </label>

                      {/* Option 2: Lowest Latency */}
                      <label className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-[#F8FAFC] transition-colors cursor-pointer bg-blue-50/50 border border-blue-100">
                        <input
                          type="radio"
                          name="routingLogic"
                          checked={routingLogic === 'latency'}
                          onChange={() => setRoutingLogic('latency')}
                          className="mt-0.5 text-[#2563EB] focus:ring-[#2563EB]"
                        />
                        <div>
                          <span className="text-xs font-bold text-[#0F172A] block">
                            Lowest Latency
                          </span>
                          <span className="text-[11px] text-[#64748B] leading-relaxed block">
                            Prioritizes response time over cost for real-time calculation applications.
                          </span>
                        </div>
                      </label>

                      {/* Option 3: Strict Provider Isolation */}
                      <label className="flex items-start gap-3 p-2.5 rounded-xl hover:bg-[#F8FAFC] transition-colors cursor-pointer">
                        <input
                          type="radio"
                          name="routingLogic"
                          checked={routingLogic === 'isolation'}
                          onChange={() => setRoutingLogic('isolation')}
                          className="mt-0.5 text-[#2563EB] focus:ring-[#2563EB]"
                        />
                        <div>
                          <span className="text-xs font-bold text-[#0F172A] block">
                            Strict Provider Isolation
                          </span>
                          <span className="text-[11px] text-[#64748B] leading-relaxed block">
                            Never routes cross-provider. Useful for strict sovereign data residency.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* GLOBAL AI SAFETY SETTINGS */}
                  <div className="space-y-3 pt-4 border-t border-[#F1F5F9]">
                    <label className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block">
                      GLOBAL AI SAFETY SETTINGS
                    </label>

                    {/* Toggle 1: PII Redaction Filter */}
                    <div className="flex items-center justify-between py-1">
                      <div>
                        <span className="text-xs font-bold text-[#0F172A] block">
                          PII Redaction Filter
                        </span>
                        <span className="text-[11px] text-[#64748B] block">
                          Scrub PII before sending to external model endpoints.
                        </span>
                      </div>
                      <button
                        onClick={() => setPiiRedaction(!piiRedaction)}
                        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                          piiRedaction ? 'bg-[#0F172A]' : 'bg-[#CBD5E1]'
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                            piiRedaction ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Toggle 2: Strict Prompt Injection Guard */}
                    <div className="flex items-center justify-between py-1">
                      <div>
                        <span className="text-xs font-bold text-[#0F172A] block">
                          Strict Prompt Injection Guard
                        </span>
                        <span className="text-[11px] text-[#64748B] block">
                          Block suspicious adversarial user inputs globally.
                        </span>
                      </div>
                      <button
                        onClick={() => setPromptInjectionGuard(!promptInjectionGuard)}
                        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                          promptInjectionGuard ? 'bg-[#0F172A]' : 'bg-[#CBD5E1]'
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                            promptInjectionGuard ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Toggle 3: Regional Sovereign Lock */}
                    <div className="flex items-center justify-between py-1">
                      <div>
                        <span className="text-xs font-bold text-[#0F172A] block">
                          Regional Sovereign Lock
                        </span>
                        <span className="text-[11px] text-[#64748B] block">
                          Confine AI tensor execution strictly to EU/US region nodes.
                        </span>
                      </div>
                      <button
                        onClick={() => setRegionalResidencyLock(!regionalResidencyLock)}
                        className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors cursor-pointer ${
                          regionalResidencyLock ? 'bg-[#0F172A]' : 'bg-[#CBD5E1]'
                        }`}
                      >
                        <div
                          className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${
                            regionalResidencyLock ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Apply Changes Button */}
                  <div className="pt-2">
                    <button
                      onClick={handleApplyConfigChanges}
                      className="w-full py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      <span>Apply Changes</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Active Model Routes Summary Cards */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                      Primary Model Routes ({modelRoutes.length})
                    </h3>
                    <p className="text-xs text-[#64748B] font-medium mt-0.5">
                      Intelligent routing gateways with active latency failovers
                    </p>
                  </div>
                  <button
                    onClick={() => setActiveTab('routes')}
                    className="text-xs font-bold text-[#2563EB] hover:underline cursor-pointer"
                  >
                    View All Routes & Latencies
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {modelRoutes.slice(0, 3).map((route) => (
                    <div
                      key={route.id}
                      className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-3 hover:border-[#2563EB] transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px] font-bold text-[#2563EB] truncate max-w-[200px]">
                          {route.routeKey}
                        </span>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-md text-[10px] font-bold">
                          {route.health}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-bold text-[#0F172A] block">{route.task}</span>
                        <span className="text-[11px] text-[#64748B] block mt-0.5">
                          Primary: <strong className="text-[#334155]">{route.primaryModelName}</strong>
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0] text-[11px] text-[#64748B]">
                        <span>P95: <strong className="text-[#0F172A]">{route.p95Latency}ms</strong></span>
                        <span>Eval: <strong className="text-emerald-700">{route.evalScore}%</strong></span>
                        <span>Cost: <strong className="text-[#0F172A]">${route.costPer1kReq}</strong>/1k</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 2: MODEL ROUTES (Cards with full telemetry)                */}
          {/* =============================================================== */}
          {activeTab === 'routes' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">AI Gateway Route Directory</h3>
                  <p className="text-xs text-[#64748B]">
                    Dynamic routing table with automatic failover, timeout limits, and real-time cost tracking.
                  </p>
                </div>
                <button
                  onClick={() => setShowNewRouteModal(true)}
                  className="px-3.5 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Route</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {modelRoutes.map((route) => (
                  <div
                    key={route.id}
                    className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4 hover:border-[#CBD5E1] transition-all"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 bg-blue-50 text-[#2563EB] font-mono text-xs font-bold rounded-md border border-blue-100">
                            {route.routeKey}
                          </span>
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
                            {route.status}
                          </span>
                        </div>
                        <h4 className="text-sm font-extrabold text-[#0F172A] mt-2">
                          {route.task}
                        </h4>
                      </div>
                      <span className="text-[11px] font-mono text-[#64748B] bg-[#F1F5F9] px-2 py-1 rounded-lg">
                        {route.trafficWeight}
                      </span>
                    </div>

                    {/* Routing Graph */}
                    <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[#64748B] text-[11px] font-bold">PRIMARY TARGET</span>
                        <span className="font-mono text-[#0F172A] font-bold">{route.primaryModelName}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#64748B] text-[11px] font-bold">FALLBACK FAILOVER</span>
                        <span className="font-mono text-[#64748B]">{route.fallbackModelName}</span>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-[#E2E8F0] text-[11px] text-[#64748B]">
                        <span>P95 Timeout Threshold</span>
                        <span className="font-mono font-bold text-[#0F172A]">{route.timeoutMs} ms</span>
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-4 gap-2 pt-2 border-t border-[#F1F5F9] text-center">
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">P95 Latency</span>
                        <span className="text-xs font-bold text-[#0F172A] block mt-0.5">{route.p95Latency}ms</span>
                      </div>
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Eval Score</span>
                        <span className="text-xs font-bold text-emerald-600 block mt-0.5">{route.evalScore}%</span>
                      </div>
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Cost / 1k Req</span>
                        <span className="text-xs font-bold text-[#0F172A] block mt-0.5">${route.costPer1kReq}</span>
                      </div>
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Health</span>
                        <span className="text-xs font-bold text-emerald-700 block mt-0.5">{route.health}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 3: PROMPTS (Prompt Library with zero private CoT exposed)   */}
          {/* =============================================================== */}
          {activeTab === 'prompts' && (
            <div className="space-y-6">
              {/* Privacy Notice Banner */}
              <div className="bg-blue-50/70 border border-blue-200/80 rounded-2xl p-4 flex items-start gap-3.5">
                <div className="p-2 bg-blue-100 text-blue-800 rounded-xl shrink-0 mt-0.5">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-blue-900">Zero Chain-of-Thought Exposure Guarantee:</span>
                    <span className="px-2 py-0.5 bg-blue-200/80 text-blue-900 rounded-md font-mono text-[10px] font-bold">
                      Strict Output Scrubbing
                    </span>
                  </div>
                  <p className="text-blue-800/90 leading-relaxed font-medium">
                    In compliance with YamooPay Security Policies, private reasoning tokens and internal Chain-of-Thought (CoT) traces are strictly filtered at the gateway proxy and are never exposed or logged in the control center UI.
                  </p>
                </div>
              </div>

              {/* Prompts Table */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
                <div className="p-4 border-b border-[#F1F5F9] flex items-center justify-between bg-white">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                      PROMPT LIBRARY
                    </span>
                    <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full text-[11px] font-bold">
                      {promptLibrary.length} Active System Prompts
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-[#F1F5F9] text-[#64748B] font-bold text-[11px] bg-[#F8FAFC]/50">
                        <th className="py-3 px-4">Prompt</th>
                        <th className="py-3 px-3">Version</th>
                        <th className="py-3 px-4">Task</th>
                        <th className="py-3 px-3 text-center">Status</th>
                        <th className="py-3 px-3 text-center">Eval</th>
                        <th className="py-3 px-4">Last Modified</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F1F5F9]">
                      {promptLibrary.map((prompt) => (
                        <tr key={prompt.id} className="hover:bg-[#F8FAFC] transition-colors">
                          {/* Prompt Key & Name */}
                          <td className="py-3.5 px-4">
                            <div>
                              <span className="font-bold text-[#0F172A] block">{prompt.name}</span>
                              <span className="font-mono text-[10px] text-[#2563EB] block mt-0.5">
                                {prompt.key}
                              </span>
                            </div>
                          </td>

                          {/* Version */}
                          <td className="py-3.5 px-3">
                            <span className="px-2 py-0.5 bg-slate-100 text-[#334155] rounded-md font-mono text-[11px] font-bold">
                              {prompt.version}
                            </span>
                          </td>

                          {/* Task */}
                          <td className="py-3.5 px-4 text-[#475569]">
                            {prompt.task}
                          </td>

                          {/* Status */}
                          <td className="py-3.5 px-3 text-center">
                            {prompt.status === 'Production' ? (
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] border border-emerald-200/60">
                                Production
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-bold text-[10px] border border-amber-200/60">
                                Staged
                              </span>
                            )}
                          </td>

                          {/* Eval */}
                          <td className="py-3.5 px-3 text-center font-bold text-emerald-700">
                            {prompt.evalScore}%
                          </td>

                          {/* Last Modified */}
                          <td className="py-3.5 px-4 text-[#64748B] text-[11px]">
                            <div>
                              <span className="block">{prompt.lastModified}</span>
                              <span className="text-[10px] text-[#94A3B8] block">by {prompt.modifiedBy}</span>
                            </div>
                          </td>

                          {/* Actions: Compare, Evaluate, Promote, Rollback */}
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => {
                                  setSelectedPromptForCompare(prompt);
                                  setShowPromptCompareModal(true);
                                }}
                                className="px-2 py-1 bg-white hover:bg-[#F1F5F9] text-[#334155] border border-[#E2E8F0] rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                                title="Compare version diffs"
                              >
                                Compare
                              </button>
                              <button
                                onClick={() => {
                                  setEvalModalSuite('eval-salary-accuracy-v4');
                                  setShowRunEvalModal(true);
                                }}
                                className="px-2 py-1 bg-white hover:bg-[#F1F5F9] text-[#2563EB] border border-blue-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                                title="Run evaluation suite"
                              >
                                Evaluate
                              </button>
                              {prompt.status !== 'Production' ? (
                                <button
                                  onClick={() => handlePromotePrompt(prompt)}
                                  className="px-2 py-1 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                                  title="Promote to Production"
                                >
                                  Promote
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleRollbackPrompt(prompt)}
                                  className="px-2 py-1 bg-white hover:bg-red-50 text-red-600 border border-red-200 rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                                  title="Rollback to previous revision"
                                >
                                  Rollback
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 4: MODEL REGISTRY (Full registry detail)                    */}
          {/* =============================================================== */}
          {activeTab === 'models' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">AI Model Registry</h3>
                  <p className="text-xs text-[#64748B]">
                    Dynamic catalog of models running across Yamoo Internal vLLM, dedicated enterprise endpoints, and sovereign clusters.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modelRegistry.map((model) => (
                  <div
                    key={model.id}
                    className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4 hover:border-[#CBD5E1] transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-[#2563EB] uppercase tracking-wider block">
                          {model.providerType}
                        </span>
                        <h4 className="text-sm font-black text-[#0F172A] mt-1">{model.name}</h4>
                        <span className="font-mono text-[11px] text-[#64748B] block mt-0.5">{model.version}</span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] border border-emerald-200/50">
                        {model.status}
                      </span>
                    </div>

                    <div className="p-3 bg-[#F8FAFC] rounded-xl space-y-1.5 text-xs text-[#475569]">
                      <div className="flex justify-between">
                        <span>Provider Endpoint:</span>
                        <strong className="text-[#0F172A]">{model.provider}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Context Window:</span>
                        <strong className="text-[#0F172A]">{model.contextWindow}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>TPM Quota Limit:</span>
                        <strong className="text-[#0F172A]">{model.tpmLimit}</strong>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#F1F5F9] text-center text-xs">
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">P95 Latency</span>
                        <span className="text-xs font-bold text-[#0F172A] block mt-0.5">{model.latencyP95} ms</span>
                      </div>
                      <div className="p-2 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">In / Out Token Cost</span>
                        <span className="text-xs font-bold text-[#0F172A] block mt-0.5">
                          ${model.costPer1kInput} / ${model.costPer1kOutput}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 5: EVALUATIONS (Real test suites without fake Gold Stds)     */}
          {/* =============================================================== */}
          {activeTab === 'evaluations' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">AI Evaluation Suites & Accuracy Benchmarks</h3>
                  <p className="text-xs text-[#64748B]">
                    Real validation test suites assessing mathematical exactness, statutory compliance, and safety guardrails.
                  </p>
                </div>
                <button
                  onClick={() => setShowRunEvalModal(true)}
                  className="px-3.5 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                >
                  <Play className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Run Evaluation Suite</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {evalSuites.map((suite) => (
                  <div
                    key={suite.id}
                    className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-[10px] font-bold text-[#2563EB] uppercase tracking-wider">
                          {suite.taskDomain}
                        </span>
                        <h4 className="text-sm font-black text-[#0F172A] mt-1">{suite.suiteName}</h4>
                        <span className="text-[11px] text-[#64748B] block mt-0.5">
                          Target: <strong className="text-[#334155]">{suite.testedModel}</strong>
                        </span>
                      </div>
                      <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] border border-emerald-200/50">
                        {suite.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-4 gap-2 text-center text-xs">
                      <div className="p-2.5 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Exact Match</span>
                        <span className="text-sm font-black text-emerald-600 block mt-0.5">{suite.exactMatchScore}%</span>
                      </div>
                      <div className="p-2.5 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Hallucination</span>
                        <span className="text-sm font-black text-[#0F172A] block mt-0.5">{suite.hallucinationRate}%</span>
                      </div>
                      <div className="p-2.5 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">P99 Latency</span>
                        <span className="text-sm font-black text-[#0F172A] block mt-0.5">{suite.p99Latency}ms</span>
                      </div>
                      <div className="p-2.5 bg-[#F8FAFC] rounded-xl">
                        <span className="text-[10px] text-[#64748B] font-bold uppercase block">Safety Guard</span>
                        <span className="text-sm font-black text-emerald-700 block mt-0.5">{suite.safetyCompliance}%</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-[#64748B] pt-2 border-t border-[#F1F5F9]">
                      <span>Test cases: <strong className="text-[#0F172A]">{suite.datasetSize.toLocaleString()}</strong></span>
                      <span>Last run: <strong className="text-[#0F172A]">{suite.lastRunDate}</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 6: RAG & KNOWLEDGE SOURCES                                  */}
          {/* =============================================================== */}
          {activeTab === 'rag' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">RAG & Vector Knowledge Repositories</h3>
                  <p className="text-xs text-[#64748B]">
                    Statutory corpora, calculation formulas, and OpenAPI embeddings powering ground-truth AI reasoning.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ragSources.map((rag) => (
                  <div
                    key={rag.id}
                    className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs space-y-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="p-2.5 bg-blue-50 text-[#2563EB] rounded-xl">
                        <Database className="w-4 h-4" />
                      </div>
                      <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px]">
                        {rag.status}
                      </span>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-[#0F172A] leading-snug">{rag.name}</h4>
                      <span className="text-[10px] text-[#64748B] font-mono block mt-1">{rag.type}</span>
                    </div>

                    <div className="p-3 bg-[#F8FAFC] rounded-xl space-y-1.5 text-xs text-[#475569]">
                      <div className="flex justify-between">
                        <span>Vector Index Size:</span>
                        <strong className="text-[#0F172A] font-mono">{rag.vectorCount}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Embedding Model:</span>
                        <strong className="text-[#0F172A]">{rag.embeddingModel}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>Index Query Latency:</span>
                        <strong className="text-[#0F172A]">{rag.indexLatency}</strong>
                      </div>
                      <div className="flex justify-between">
                        <span>30d Hit Rate:</span>
                        <strong className="text-emerald-700 font-bold">{rag.hitRate30d}%</strong>
                      </div>
                    </div>

                    <div className="text-[11px] text-[#94A3B8] pt-2 border-t border-[#F1F5F9]">
                      Last incremental sync: {rag.lastSync}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* TAB 7: COSTS & USAGE                                            */}
          {/* =============================================================== */}
          {activeTab === 'costs' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase">MTD Spend Trajectory</span>
                  <div className="text-2xl font-black text-[#0F172A] mt-2">$2,418.50</div>
                  <span className="text-xs text-emerald-600 font-bold block mt-1">18% under allocated monthly budget</span>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase">Cost per 1,000 API Calls</span>
                  <div className="text-2xl font-black text-[#0F172A] mt-2">$0.038</div>
                  <span className="text-xs text-[#64748B] block mt-1">Optimized via Yamoo Fast Router caching</span>
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-2xs">
                  <span className="text-[11px] font-bold text-[#64748B] uppercase">Cache Hit Ratio</span>
                  <div className="text-2xl font-black text-[#0F172A] mt-2">64.2%</div>
                  <span className="text-xs text-emerald-600 font-bold block mt-1">Saved ~520M tokens this cycle</span>
                </div>
              </div>
            </div>
          )}

          {/* =============================================================== */}
          {/* BOTTOM SECTION: RECENT AI PLATFORM ACTIONS (Audit Log)         */}
          {/* =============================================================== */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            <div className="p-4 border-b border-[#F1F5F9] flex items-center justify-between bg-white">
              <span className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                Recent AI Platform Actions
              </span>
              <button
                onClick={() => onNavigateToView('admin-audit')}
                className="text-xs font-bold text-[#2563EB] hover:underline cursor-pointer"
              >
                View Full Audit Log
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-bold text-[11px] bg-[#F8FAFC]/50">
                    <th className="py-3 px-4">TIMESTAMP (UTC)</th>
                    <th className="py-3 px-4">ACTOR</th>
                    <th className="py-3 px-4">NATIVE ACTION</th>
                    <th className="py-3 px-4">TARGET RESOURCE</th>
                    <th className="py-3 px-4 text-right">RESULT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-[#F8FAFC] transition-colors">
                      {/* Timestamp */}
                      <td className="py-3.5 px-4 font-mono text-[#64748B] text-[11px]">
                        {log.timestamp}
                      </td>

                      {/* Actor */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              log.result === 'Success'
                                ? 'bg-emerald-500'
                                : log.result === 'Denied (Policy)'
                                ? 'bg-red-500'
                                : 'bg-blue-500'
                            }`}
                          />
                          <span className="font-bold text-[#0F172A]">{log.actor}</span>
                        </div>
                      </td>

                      {/* Native Action */}
                      <td className="py-3.5 px-4 font-mono font-bold text-[#2563EB] text-[11px]">
                        {log.nativeAction}
                      </td>

                      {/* Target Resource */}
                      <td className="py-3.5 px-4 font-mono text-[#475569] text-[11px]">
                        {log.targetResource}
                      </td>

                      {/* Result */}
                      <td className="py-3.5 px-4 text-right">
                        {log.result === 'Success' ? (
                          <span className="text-emerald-700 font-bold text-[11px]">
                            Success
                          </span>
                        ) : log.result === 'Denied (Policy)' ? (
                          <span className="text-red-600 font-bold text-[11px]">
                            Denied (Policy)
                          </span>
                        ) : (
                          <span className="text-amber-700 font-bold text-[11px]">
                            Warning
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* MODAL: NEW ROUTE PROVISIONING                                   */}
      {/* =============================================================== */}
      {showNewRouteModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl max-w-lg w-full shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4">
              <div>
                <h3 className="text-base font-extrabold text-[#0F172A]">Provision New AI Gateway Route</h3>
                <p className="text-xs text-[#64748B]">Define route identifier, task scope, and failover model pair.</p>
              </div>
              <button
                onClick={() => setShowNewRouteModal(false)}
                className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateRoute} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-[#334155] block mb-1">
                  Internal Route Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newRouteKey}
                  onChange={(e) => setNewRouteKey(e.target.value)}
                  placeholder="e.g. route.salary.custom_withholding"
                  className="w-full px-3.5 py-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs font-mono text-[#0F172A] focus:bg-white focus:border-[#2563EB] outline-none"
                />
              </div>

              <div>
                <label className="font-bold text-[#334155] block mb-1">
                  Task Domain Description <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newRouteTask}
                  onChange={(e) => setNewRouteTask(e.target.value)}
                  placeholder="e.g. Custom Multi-Tier State Tax Deductions"
                  className="w-full px-3.5 py-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:bg-white focus:border-[#2563EB] outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-[#334155] block mb-1">Primary Model</label>
                  <select
                    value={newRoutePrimary}
                    onChange={(e) => setNewRoutePrimary(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:bg-white outline-none"
                  >
                    {modelRegistry.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-bold text-[#334155] block mb-1">Fallback Model</label>
                  <select
                    value={newRouteFallback}
                    onChange={(e) => setNewRouteFallback(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs text-[#0F172A] focus:bg-white outline-none"
                  >
                    {modelRegistry.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-4 border-t border-[#F1F5F9] flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewRouteModal(false)}
                  className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#334155] font-bold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl"
                >
                  Create Route
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL: PROMPT COMPARE & DIFF (No Private CoT)                   */}
      {/* =============================================================== */}
      {showPromptCompareModal && selectedPromptForCompare && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl max-w-2xl w-full shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4">
              <div>
                <span className="text-[10px] font-bold text-[#2563EB] uppercase tracking-wider">Prompt Diff Inspector</span>
                <h3 className="text-base font-extrabold text-[#0F172A]">{selectedPromptForCompare.name}</h3>
                <span className="font-mono text-[11px] text-[#64748B]">{selectedPromptForCompare.key} ({selectedPromptForCompare.version})</span>
              </div>
              <button
                onClick={() => setShowPromptCompareModal(false)}
                className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between text-[#64748B]">
                <span>Status: <strong className="text-emerald-700">{selectedPromptForCompare.status}</strong></span>
                <span>Eval Benchmark: <strong className="text-[#0F172A]">{selectedPromptForCompare.evalScore}%</strong></span>
                <span>Avg Latency: <strong className="text-[#0F172A]">{selectedPromptForCompare.latencyAverage}ms</strong></span>
              </div>

              <div>
                <label className="font-bold text-[#334155] block mb-1">System Prompt Template (Sanitized):</label>
                <div className="p-3 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl font-mono text-[11px] text-[#0F172A] leading-relaxed whitespace-pre-wrap">
                  {selectedPromptForCompare.templateSnippet}
                </div>
              </div>

              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-[11px] flex items-center gap-2 font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Zero Chain-of-Thought policy enforced: all intermediate reasoning remains private and protected.</span>
              </div>
            </div>

            <div className="pt-4 border-t border-[#F1F5F9] flex items-center justify-end gap-2 text-xs">
              <button
                type="button"
                onClick={() => setShowPromptCompareModal(false)}
                className="px-4 py-2 bg-[#0F172A] text-white font-bold rounded-xl"
              >
                Close Inspector
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL: RUN EVALUATION SUITE                                     */}
      {/* =============================================================== */}
      {showRunEvalModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl max-w-md w-full shadow-2xl p-6 space-y-4 animate-in fade-in zoom-in-95 duration-150 text-xs">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <h3 className="text-sm font-extrabold text-[#0F172A]">Trigger AI Evaluation Suite</h3>
              <button onClick={() => setShowRunEvalModal(false)} className="text-[#94A3B8]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="font-bold text-[#334155] block mb-1">Select Benchmark Suite:</label>
                <select
                  value={evalModalSuite}
                  onChange={(e) => setEvalModalSuite(e.target.value)}
                  className="w-full p-2 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl text-xs"
                >
                  <option value="eval-salary-accuracy-v4">Salary Exactness Benchmark (5,000 cases)</option>
                  <option value="eval-tax-edge-cases">Cross-Border VAT Edge Cases (3,200 cases)</option>
                  <option value="eval-pii-sanitization">PII Leakage & Sanitization (8,400 cases)</option>
                  <option value="eval-jailbreak-v5">Adversarial Jailbreak Guard (12,500 cases)</option>
                </select>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl text-blue-800 text-[11px] leading-relaxed">
                Evaluation will run asynchronously across isolated worker nodes. Results and exact-match statistics will sync automatically to the telemetry dashboard.
              </div>
            </div>

            <div className="pt-3 border-t border-[#F1F5F9] flex justify-end gap-2">
              <button
                onClick={() => setShowRunEvalModal(false)}
                className="px-3.5 py-1.5 bg-[#F1F5F9] text-[#334155] font-bold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRunEvalModal(false);
                  triggerToast('Evaluation suite launched! Telemetry scoring active.');
                }}
                className="px-3.5 py-1.5 bg-[#0F172A] text-white font-bold rounded-xl flex items-center gap-1"
              >
                <Play className="w-3.5 h-3.5 text-emerald-400" />
                <span>Launch Evaluation</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL: EXPORT TELEMETRY                                         */}
      {/* =============================================================== */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl max-w-md w-full shadow-2xl p-6 space-y-4 text-xs animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <h3 className="text-sm font-extrabold text-[#0F172A]">Export AI Gateway Telemetry</h3>
              <button onClick={() => setShowExportModal(false)} className="text-[#94A3B8]">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 text-[#475569]">
              <p>Export aggregated route latencies, token consumption, PII scrub counts, and evaluation metrics.</p>
              <div className="p-3 bg-[#F8FAFC] rounded-xl space-y-1 font-mono text-[11px]">
                <div>• Format: JSON / OpenTelemetry Protocol</div>
                <div>• Period: Last 30 Days (1.2M invocations)</div>
                <div>• Redaction: All customer payloads scrubbed</div>
              </div>
            </div>

            <div className="pt-3 border-t border-[#F1F5F9] flex justify-end gap-2">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-3.5 py-1.5 bg-[#F1F5F9] text-[#334155] font-bold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowExportModal(false);
                  triggerToast('Telemetry dataset exported successfully (JSON OTel format).');
                }}
                className="px-3.5 py-1.5 bg-[#0F172A] text-white font-bold rounded-xl flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Download Telemetry</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
